import { isEmpty } from 'lodash-es';
import Swal from 'sweetalert2';
import { keyTracker } from "../utilities/shortcuts";

export default class {
  constructor($el, options) {
    this.$scope = $el;
    if ($el.dataset.manageableCards === 'initialized') return;

    options['hideBsModal'] = () => this.update();
    options['removeCard'] = () => this.update();
    this.$emptyMessage = this.$scope.querySelector('[data-manageable-message="empty"]');
    this.$scope.querySelectorAll(this.$scope.dataset.manageableActionsSelector).forEach(($a) => {
      $a.addEventListener('add-row:appended', (ev) => this.onNewEntry(ev, options));
    });

    const observer = new MutationObserver(() => this.update());
    observer.observe(this.$scope.querySelector('.entries-cards'), { childList: true, subtree: true });

    $el.dataset.manageableCards = 'initialized';
    this.$scope.querySelectorAll(`.entries-cards ${this.$scope.dataset.manageableCardsSelector}`).forEach(($el) => new Card($el, options));
  }

  onNewEntry(ev, options) {
    let card = new Card(ev.detail.row, options);

    if (!!ev.target.dataset.hideModal)
      return;

    card.showModal();
  }

  update() {
    return this.$emptyMessage.classList.toggle('hidden', this.$scope.querySelectorAll('.entry-card').length > 0);
  }
}

class Card {
  constructor($card, options) {
    this.$card = $card.closest('.entry-card')
    this.$modal = this.$card.querySelector('.modal');

    this.requiredFields = options['requiredFields'];
    this.callbacks = this.defineCallbacks(options);

    $(this.$modal).on('keydown', keyTracker('enter', (ev) => this.$card.querySelector('[data-manageable-action="save"]').click()));
    $(this.$modal).on('keydown', keyTracker('esc', (ev) => this.onCloseModal(ev)));
    $(this.$modal).on('hide.bs.modal', (ev) => this.callbacks.hideBsModal(ev));
    ['remove', 'edit', 'save', 'close'].forEach((event) => this.$card.querySelectorAll(`[data-manageable-action="${event}"]`).forEach(($el) => $el.addEventListener('click', (ev) => this[event](ev))));
    this.$card.addEventListener('printOutput', () => this.callbacks.printOutput(this.$card));

    this.callbacks.printOutput(this.$card);
    this.callbacks.initialize(this.$card);
  }

  defineCallbacks(callbacks) {
    return {
      initialize: this.proxyCallback(callbacks, 'initialize'),
      onModalClose: this.proxyCallback(callbacks, 'onModalClose'),
      hideBsModal: this.proxyCallback(callbacks, 'hideBsModal'),
      onRemove: this.proxyCallback(callbacks, 'onRemove'),
      removeCard: this.proxyCallback(callbacks, 'removeCard'),
      printOutput: (callbacks['printOutput'] || this.printOutput.bind(this)),
      validate: (callbacks['validate'] || (this.validate.bind(this)))
    }
  }

  proxyCallback(callbacks, opt) {
    return callbacks[opt] || this.noop();
  }

  noop() {
    return (() => {});
  }

  showModal() {
    $(this.$modal).modal('show');
  }

  hideModal() {
    this.callbacks.printOutput(this.$card);
    $(this.$modal).modal('hide');
  }

  onCloseModal() {
    $(this.$modal).modal('hide');
  }

  findModal() {
    return this.$card.querySelector('.modal');
  }

  removeCard() {
    this.hideModal();
    setTimeout(() => {
      this.callbacks.onModalClose(this.$card);
      const event = new CustomEvent('card:removed', { detail: { card: this.$card } });
      this.$card.remove();
      document.dispatchEvent(event);
      this.callbacks.removeCard();
    }, 130);
  }

  validate($card, highLightError = true) {
    return [...$card.querySelectorAll(this.requiredFields)].reduce((accumulator, $el) => {
      let elValid = !isEmpty($el.value);

      if (highLightError) {
        $el.closest('.form-group').classList.toggle('error', !elValid);
        $el.closest('.form-group').classList.toggle('has-error', !elValid);
      }

      return accumulator &= elValid;
    }, true);
  }

  printOutput($card) {
    $card.querySelectorAll('[data-manageable-output]').forEach(($el) => {
      let output = $el.dataset.manageableOutput,
        matches = output.matchAll(/(\${(.*?)})/g);

      for(let match of matches) {
        output = output.replace(match[0], $card.querySelector(match[2]).value);
      }

      $el.innerHTML = output;
    });
  }

  remove(ev) {
    console.log('on remove')
    Swal.fire({
      title: 'Atenção',
      html: 'Deseja realmente remover?',
      showClass: { backdrop: 'swal2-noanimation', popup: '', },
      hideClass: { popup: '' },
      customClass: {
        cancelButton: 'btn btn-gray-primary',
        confirmButton: 'btn btn-primary mr-3'
      },
      buttonsStyling: false,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.value) return;

      // Persisted cards with a dedicated destroy endpoint (e.g. guardian cards) fire an
      // immediate DELETE so the removal is auditable on the server side. Other persisted
      // cards still fall back to the legacy `_destroy` hidden-input pattern, which is
      // honored by `accepts_nested_attributes_for :..., allow_destroy: true` on their parent.
      if (!isEmpty(this.$card.dataset.destroyUrl)) {
        this.deleteRemote(this.$card.dataset.destroyUrl);
        return;
      }

      if (!isEmpty(this.$card.dataset.objectId)) {
        let nodes = `<input type="hidden" name="${this.$card.dataset.objectName}[_destroy]" value="1">`;
        nodes += `<input type="hidden" name="${this.$card.dataset.objectName}[id]" value="${this.$card.dataset.objectId}">`;
        this.$card.closest('.entries-cards').insertAdjacentHTML('afterbegin', nodes);
      }

      this.removeCard();
    });
  }

  deleteRemote(url) {
    const csrf = document.querySelector('meta[name="csrf-token"]');
    fetch(url, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrf ? csrf.content : ''
      }
    }).then((res) => {
      if (!res.ok) {
        return Swal.fire({
          title: 'Erro',
          html: 'Não foi possível remover. Tente novamente em instantes.',
          type: 'error',
          buttonsStyling: false,
          customClass: { confirmButton: 'btn btn-primary' }
        });
      }
      this.removeCard();
    }).catch(() => {
      Swal.fire({
        title: 'Erro',
        html: 'Falha de conexão ao remover.',
        type: 'error',
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary' }
      });
    });
  }

  edit(ev) {
    ev.preventDefault();
    this.showModal();
  }

  save(ev) {
    if (!this.callbacks.validate(this.$card)) {
      return Swal.fire({
        title: 'Atenção',
        type: 'warning',
        text: 'Por favor, preencha os campos obrigatórios em destaque',
        showClass: { backdrop: 'swal2-noanimation', popup: '', },
        hideClass: { popup: '' },
        customClass: {
          cancelButton: 'btn btn-gray-primary',
          confirmButton: 'btn btn-primary mr-3'
        },
        buttonsStyling: false,
      }) && ev.preventDefault();
    }

    this.hideModal();
    this.$card.querySelectorAll('.inner-card.hidden').forEach(($el) => $el.classList.toggle('hidden', false));
  }

  close(ev) {
    if (ev.target.classList.contains('with-date-picker')) return ev.preventDefault() && ev.stopPropagation();
    if (!this.callbacks.validate(this.$card, false)) return this.removeCard();

    this.hideModal();
  }
}